/**
 * Angular directive to handle autofilling data during development. When
 * included in a `show` page in the apply process, this directive adds an
 * Autofill element to the bottom right of the screen, which features a list of
 * personas, meaning data payloads that will result in expected outcomes when
 * using the application.
 *
 * The data is application-specific and is loaded at load time by the
 * `CustomerApplicationEngine` from the configuration files appropriate to that
 * application.
 *
 * To update autofill data, edit the data for the autofill personas in
 * `config/customer_application/<APPLICATION>/v/<VERSION>/autofill/`.
 */

/**
 * Check the validity of the autofill data by seeing if any `"mismatch"` values
 * exist in the payload. If so, print an alert box explaining the issue so that
 * the Frontend Customer–Facing team can be told. Evaluated for side effects.
 *
 * @param {string} The autofill data passed in from `config`.
 */
const checkValidity = autofillData => {
  if (!autofillData.mismatches) {
    return;
  }

  const mismatches = JSON.parse(autofillData.mismatches);

  const messageContents = Object.entries(mismatches)
    .map(([identity, mismatchData]) => {
      const identityString = JSON.parse(identity) // This is doubly JSON'd
        .map(id => id.join(": "))
        .join(", ");
      const mismatchString = mismatchData.join(", ");
      return (
        "The persona with " +
        identityString +
        " has a mismatch in the following fields: " +
        mismatchString
      );
    })
    .join("\n\n");

  if (messageContents.length) {
    window.alert(
      "The data used by the autofill system may be out of sync with the " +
        "data used on the backend. You may see unexpected results. Please " +
        "let the Fullstack team know about this message.\n\nThe mismatched " +
        "data is as follows: \n\n" +
        messageContents
    );
  }
};

angular
  .module("avant.customer_applications.directives")
  .directive("amountAutofill", [
    "RandomizationService",
    "$stateParams",
    (RandomizationService, $stateParams) => {
      return {
        restrict: "E",
        scope: false, // Share scope with parent
        template: `
          <div style="position: fixed;
                      bottom: 0;
                      right: 0;
                      z-index: 9999;
                      background-color: rgba(255, 255, 255, .6);
                      margin: 0;
                      padding: 1em">
            <h2 class="form__header headline headline--medium">
              Autofill
            </h2>
            <p>
              Stage:
              <br/>
              <code>{{ $stateParams.stage }}</code>
            </p>
            <div class="form__label label label--large">
              Autofill persona
            </div>
            <div class="form__row">
              <div class="form__group">
                <select class="form-control form__select"
                        id="autofill-select"
                        ng-options="name for name in options"
                        ng-model="selectedPersona">
                </select>
              </div>
            </div>
            <div class="form__row">
              <div class="form__group">
                <button id="autofill-fill-button"
                        class="button button--primary button-right"
                        ng-click="fill()">
                  Fill
                </button>
              </div>
            </div>
          </div>
        `,
        link: (scope, _element, attrs) => {
          const DEFAULT_SELECTION = "Random";

          const autofillData = JSON.parse(attrs.autofillData);
          scope.options = Object.keys(autofillData.personas);
          scope.personas = autofillData.personas;
          checkValidity(autofillData);

          // Initialize to Random persona if there is one.
          scope.selectedPersona = scope.options.includes(DEFAULT_SELECTION)
            ? DEFAULT_SELECTION
            : scope.options[0];

          scope.$stateParams = $stateParams;

          // Initialize to Random persona if there is one.
          scope.selectedPersona = scope.options.includes(DEFAULT_SELECTION)
            ? DEFAULT_SELECTION
            : scope.options[0];

          /**
           * Randomize the fields that call for randomization in the persona
           * data and remove the portion of the persona payload that describes
           * which fields should be randomized.
           *
           * @param {object} rawData - Data about the persona before
           *   randomizing the fields specified for randomization.
           * @return {object} Data about the persona after randomizing the
           *   fields specified for randomization.
           */
          const randomize = rawData => {
            // Only randomize for personae with randomized data.
            if (rawData.random !== undefined) {
              rawData.random.forEach(randomizedField => {
                const randomValue = RandomizationService.randomData(
                  randomizedField.randomizer
                );

                const paths = randomizedField.paths;

                // Here's a hack to be able to mutate the input data and update
                // it with randomized data in the appropriate fields.
                //
                // Consider this code:
                //
                // ```
                // let foo = { bar: { baz: { quux: 3 } } }
                // const path = ["bar", "baz", "quux"]
                // ```
                //
                // If we want a pointer to mutate the `foo` object, we CANNOT
                // do this, putting each element of the path into bracket
                // notation:
                //
                // ```
                // let toUpdate = foo["bar"]["baz"]["quux"];
                // toUpdate = 7;
                // ```
                //
                // This fails because `toUpdate` holds a copy of 7 rather than
                // a pointer to the 7 value. Instead we have to dig down till
                // we get to the next-to-last element, and update using bracket
                // notation, so that we still have a pointer to the original
                // inner object rather than a copy of its value:
                //
                // ```
                // let toUpdate = foo["bar"]["baz"];
                // toUpdate["quux"] = 7;
                // ```
                //
                // This code does that, by getting all but the final element of
                // the path, and holding onto that element in order to still
                // have a pointer into the innermost object.
                paths.forEach(path => {
                  const innerObject = path
                    .slice(0, -1)
                    .reduce((a, e) => a[e], rawData);
                  innerObject[path.slice(-1)] = randomValue;
                });
              });
            }

            return rawData;
          };

          /**
           * Fill in data appropriate to the current stage based on the persona
           * data selected in the select box. Evaluated for side effects in
           * DOM.
           */
          scope.fill = () => {
            const personaDataForStage = randomize(
              scope.personas[scope.selectedPersona]
            )[scope.$stateParams.stage];

            // Regrettably, `ContractCtrl.coffee` marches to the beat of its own
            // drummer, behaving differently than all the other stages. It
            // maintains local copies of the variables that in other stages are
            // stored in the `ApplicationCtrl`. So in addition to updating the
            // partial application on `scope` here, we also broadcast the same
            // data so that `ContractCtrl` can subscribe to it and update its
            // local copies of the data.
            //
            // N.B.: `bank_account` stage also behaves oddly and is similar
            // to the `contract` page.
            //
            // This should be irrelevant in other stages, and so this can be put
            // inside an `if` block that checks whether we're on the `contract`
            // stage if a compelling reason emerges for doing so, but the goal
            // was to confine special-case logic to the controller that's
            // already doing something weird rather than adding coupling and
            // letting its weirdness pollute this code, too.
            //
            // TODO: Refactor `ContractCtrl.coffee` to follow the usual way of
            // updating the partial application.
            scope.$root.$broadcast("autofill:fill", {
              stage: scope.$stateParams.stage,
              data: personaDataForStage
            });

            if (personaDataForStage) {
              Object.entries(personaDataForStage).forEach(
                ([category, value]) => {
                  scope[category] = { ...scope[category], ...value };
                }
              );
            }
          };
        }
      };
    }
  ]);
